import React, { useEffect } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useNavigate
} from "react-router-dom";
import AuthenticatedLayout from "./AuthenticatedLayout";
import {
  Login,
  TwoFactorOTP,
  ChangePassword,
  Dashboard,
  UserManagement,
  UserDetails,
  PromcodeManagement,
  SubscriptionPlanManagement,
  PaymentsInformation,
  Profile
} from "./pages";
import "./App.scss";
import { AuthProvider, useAuth } from "./AuthProvider";
import { jwtDecode } from "jwt-decode"; // ✅ CORRECT
import { UserProvider } from "./UserContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AddPaymentMethodPage from "./pages/stripe/AddPaymentMethodPage";
import MakePaymentPage from "./pages/stripe/MakePaymentPage";
import CreateSubscriptionPage from "./pages/stripe/CreateSubscriptionPage";
import StripeReturnPage from "./pages/stripe/StripeReturnPage";

interface AuthContextType {
  isUserLoggedIn: boolean;
  logout: () => void;
}

interface JwtPayload {
  exp: number;
}

const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<JwtPayload>(token);
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
};

const AppContent: React.FC = () => {
  const { isUserLoggedIn, logout } = useAuth() as AuthContextType;
  const navigate = useNavigate();

  // useEffect(() => {
  //   const checkToken = () => {
  //     const token = localStorage.getItem("jwtToken");
  //     if (!token || isTokenExpired(token)) {
  //       localStorage.removeItem("jwtToken");
  //       logout(); // call the context logout
  //       navigate("/login");
  //     }
  //   };
  //
  //     checkToken(); // initial check
  //
  //     const interval = setInterval(checkToken, 10000); // Check every 10s
  //     return () => clearInterval(interval); // Cleanup on unmount
  //   }, [logout, navigate]);

  return (
    <Routes>
      {isUserLoggedIn ? (
        <>
          {/* Public Stripe routes accessible regardless of admin auth */}
          <Route path="/add-payment-method" element={<AddPaymentMethodPage />} />
          <Route path="/make-payment" element={<MakePaymentPage />} />
          <Route path="/subscribe" element={<CreateSubscriptionPage />} />
          <Route path="/stripe/return" element={<StripeReturnPage />} />
          <Route path="/" element={<Navigate replace to="/dashboard" />} />
          <Route
            path="/*"
            element={
              <AuthenticatedLayout>
                <Routes>
                  <Route path="/dashboard" element={<Dashboard />} />
                  <Route path="/user-management" element={<UserManagement />} />
                  <Route
                    path="/user-management/:userId"
                    element={<UserDetails />}
                  />
                  <Route
                    path="/promocode-management"
                    element={<PromcodeManagement />}
                  />
                  <Route
                    path="/subscription-plan-management"
                    element={<SubscriptionPlanManagement />}
                  />
                  <Route
                    path="/payments-information"
                    element={<PaymentsInformation />}
                  />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/change-password" element={<ChangePassword />} />
                </Routes>
              </AuthenticatedLayout>
            }
          />
        </>
      ) : (
        <>
          {/* Public Stripe routes accessible without admin auth */}
          <Route path="/add-payment-method" element={<AddPaymentMethodPage />} />
          <Route path="/make-payment" element={<MakePaymentPage />} />
          <Route path="/subscribe" element={<CreateSubscriptionPage />} />
          <Route path="/stripe/return" element={<StripeReturnPage />} />
          <Route path="/" element={<Login />} />
          <Route path="/otp-authentication" element={<TwoFactorOTP />} />
          <Route path="/*" element={<Navigate replace to="/" />} />
        </>
      )}
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <AuthProvider>
        <UserProvider>
          <AppContent />
          <ToastContainer
            position="top-right"
            autoClose={5000}
            hideProgressBar={false}
            newestOnTop={false}
            closeOnClick={true}
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </UserProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
